import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../ui';
import { buildFarmGrouping } from '../../utils/areaOverlayUtils';
import './StationListPanel.css';

function formatTimeAgo(date, t) {
  if (!date) return t('stations.never');
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);
  if (seconds < 60) return t('stations.time.secondsAgo', { count: seconds });
  if (seconds < 3600) return t('stations.time.minutesAgo', { count: Math.floor(seconds / 60) });
  if (seconds < 86400) return t('stations.time.hoursAgo', { count: Math.floor(seconds / 3600) });
  return t('stations.time.daysAgo', { count: Math.floor(seconds / 86400) });
}

export function StationListPanel({ farm, stations, selectedStation, isOpen, onToggle, onStationClick }) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  
  // Filter stations by search
  const filteredStations = stations.filter(station => {
    const matchesSearch = !searchQuery || 
      station.stationCode.toLowerCase().includes(searchQuery.toLowerCase()) ||
      station.locationDescription?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = !statusFilter || station.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });
  
  const counts = {
    active: stations.filter(s => s.status === 'active').length,
    warning: stations.filter(s => s.status === 'warning').length,
    inactive: stations.filter(s => s.status === 'inactive').length
  };

  const grouping = useMemo(() => {
    return buildFarmGrouping({
      farm,
      sections: farm?.sections || [],
      stations: filteredStations,
    });
  }, [farm, filteredStations]);

  const getGroupStations = (group) => {
    if (Array.isArray(group.stations)) return group.stations;
    if (Array.isArray(group.sections)) {
      return group.sections.flatMap(section => section.stations || []);
    }
    return [];
  };

  const renderStationItem = (station) => (
    <button
      key={station.id}
      className={`station-list-panel__item ${
        selectedStation?.id === station.id ? 'station-list-panel__item--selected' : ''
      }`}
      onClick={() => onStationClick(station)}
      data-testid={`station-item-${station.id}`}
    >
      <div 
        className="station-list-panel__item-dot"
        style={{
          backgroundColor: 
            station.status === 'active' ? '#5A7A3A' :
            station.status === 'warning' ? '#8A7A4A' : '#8A4A3A'
        }}
      />
      <div className="station-list-panel__item-content">
        <div className="station-list-panel__item-header">
          <div className="station-list-panel__item-code">{station.stationCode}</div>
          <Badge 
            variant={station.sensorType === 'campo_aperto' ? 'success' : 'info'}
            size="sm"
          >
            {station.sensorType === 'campo_aperto'
              ? t('stations.types.campo_aperto')
              : t('stations.types.retro_modulo')}
          </Badge>
        </div>
        {station.locationDescription && (
          <div className="station-list-panel__item-location">
            {station.locationDescription}
          </div>
        )}
        <div className="station-list-panel__item-time">
          {formatTimeAgo(station.lastTransmission, t)}
        </div>
      </div>
    </button>
  );

  const renderSectionGroup = (section, nested = false) => {
    const sectionStations = getGroupStations(section);
    if (sectionStations.length === 0) return null;
    return (
      <div
        key={section.id || section.areaName}
        className={`station-list-panel__group ${nested ? 'station-list-panel__group--nested' : ''}`}
      >
        <div className="station-list-panel__group-header">
          <span>{section.areaName || section.name}</span>
          <span className="station-list-panel__group-count">
            {sectionStations.length}
          </span>
        </div>
        <div className="station-list-panel__group-content">
          {sectionStations.map(renderStationItem)}
        </div>
      </div>
    );
  };

  const renderZoneGroup = (zone, nested = false) => {
    const zoneStations = getGroupStations(zone);
    if (zoneStations.length === 0) return null;
    return (
      <div
        key={zone.id || zone.areaName}
        className={`station-list-panel__group ${nested ? 'station-list-panel__group--nested' : ''}`}
      >
        <div className="station-list-panel__group-header">
          <span>{zone.areaName || zone.name}</span>
          <span className="station-list-panel__group-count">
            {zoneStations.length}
          </span>
        </div>
        <div className="station-list-panel__group-content">
          {(zone.sections || []).map(section => renderSectionGroup(section, true))}
        </div>
      </div>
    );
  };

  const renderQuadrantGroup = (quadrant) => {
    const quadrantStations = getGroupStations(quadrant);
    if (quadrantStations.length === 0) return null;
    return (
      <div key={quadrant.id || quadrant.areaName} className="station-list-panel__group">
        <div className="station-list-panel__group-header">
          <span>{quadrant.areaName || quadrant.name}</span>
          <span className="station-list-panel__group-count">
            {quadrantStations.length}
          </span>
        </div>
        <div className="station-list-panel__group-content">
          {(quadrant.zones || []).map(zone => renderZoneGroup(zone, true))}
        </div>
      </div>
    );
  };
  
  return (
    <>
      {/* Toggle Button - Positioned based on panel state */}
      <button
        className={`station-list-panel__toggle ${isOpen ? 'station-list-panel__toggle--open' : ''}`}
        onClick={onToggle}
        title={isOpen ? t('map.stationList.hide') : t('map.stationList.show')}
        data-testid="panel-toggle"
      >
        {isOpen ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
      </button>
      
      {/* Panel */}
      <div className={`station-list-panel ${isOpen ? 'station-list-panel--open' : ''}`}>
        {/* Search */}
        <div className="station-list-panel__search">
          <Search size={16} className="station-list-panel__search-icon" />
          <input
            type="text"
            placeholder={t('stations.searchStations')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="station-list-panel__search-input"
          />
        </div>
        
        {/* Filter Pills */}
        <div className="station-list-panel__filters">
          <button
            className={`station-list-panel__filter-pill ${!statusFilter ? 'station-list-panel__filter-pill--active' : ''}`}
            onClick={() => setStatusFilter(null)}
          >
            {t('common.all')} ({stations.length})
          </button>
          <button
            className={`station-list-panel__filter-pill station-list-panel__filter-pill--active-status ${statusFilter === 'active' ? 'station-list-panel__filter-pill--active' : ''}`}
            onClick={() => setStatusFilter(statusFilter === 'active' ? null : 'active')}
          >
            {t('stations.status.active')} ({counts.active})
          </button>
          {counts.warning > 0 && (
            <button
              className={`station-list-panel__filter-pill station-list-panel__filter-pill--warning ${statusFilter === 'warning' ? 'station-list-panel__filter-pill--active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'warning' ? null : 'warning')}
            >
              {t('stations.status.warning')} ({counts.warning})
            </button>
          )}
          {counts.inactive > 0 && (
            <button
              className={`station-list-panel__filter-pill station-list-panel__filter-pill--inactive ${statusFilter === 'inactive' ? 'station-list-panel__filter-pill--active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'inactive' ? null : 'inactive')}
            >
              {t('stations.status.inactive')} ({counts.inactive})
            </button>
          )}
        </div>
        
        {/* Station List */}
        <div className="station-list-panel__list">
          {grouping.groupingLevel === 'quadrant' &&
            grouping.quadrants.map(renderQuadrantGroup)}
          {grouping.groupingLevel === 'zone' &&
            grouping.zones.map(zone => renderZoneGroup(zone))}
          {grouping.groupingLevel === 'section' &&
            grouping.sections.map(section => renderSectionGroup(section))}
        </div>
      </div>
    </>
  );
}